import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAllTickets, updateTicket, createTicket } from '../../api/services/ticketsService';
import { getAllUsersWithToken } from '../../api/services/usersService';
import { getRentalById } from '../../api/services/rentalsService';
import { VEHICLE_TYPE_LABELS } from '../../viewmodels/InternalViewModel';
import SupportConversations from './SupportConversations';
import { getSupportConversations } from '../../api/services/supportChatService';
import { AREA, Avatar, BigButton, Card, CloseButton, FIELD, FieldLabel, FilterChip, HeaderActions, ICONS, LineIcon, LinkButton, Modal, SectionLabel, Segmented, StatusPill, VehicleThumb, type Tone } from '../../components/ui/dg';
import HelpFeedback from './HelpFeedback';

const CATEGORY_LABELS: Record<string, string> = {
  vehicle_issue: 'Véhicule',
  payment_issue: 'Paiement',
  account_issue: 'Compte',
  damage_dispute: 'Litige dégâts',
  other: 'Autre',
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Ouvert',
  in_progress: 'En cours',
  resolved: 'Résolu',
  closed: 'Fermé',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Basse',
  normal: 'Normale',
  high: 'Haute',
  urgent: 'Urgente',
};

interface AppUser {
  id_user: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  profile_photo?: string | null;
}

type Chip = 'todo' | 'urgent' | 'in_progress' | 'done' | 'all';
const PRIORITY_TONE: Record<string, Tone> = { urgent: 'red', high: 'amber', normal: 'grey', low: 'grey' };
const STATUS_TONE: Record<string, Tone> = { open: 'amber', in_progress: 'indigo', resolved: 'green', closed: 'grey' };
const COLS = 'grid-cols-[minmax(0,2.3fr)_1fr_0.8fr_0.8fr_0.9fr_16px]';
const TONES = ['orange', 'green', 'indigo', 'grey'] as const;
const toneOf = (id: string) => TONES[id.split('').reduce((sum, c) => sum + c.charCodeAt(0), 0) % TONES.length];
const photoUrl = (url?: string | null) => (!url ? null : /^https?:\/\//.test(url) ? url : `${process.env.REACT_APP_API_URL || ''}${url.replace(/^\//, '')}`);
const ago = (value?: string) => {
  if (!value) return '—';
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (!Number.isFinite(minutes)) return '—';
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  if (minutes < 24 * 60) return `il y a ${Math.round(minutes / 60)} h`;
  if (minutes < 48 * 60) return 'hier';
  return new Date(value).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

interface Ticket {
  id_ticket: string;
  user_id: string;
  rental_id?: string;
  subject: string;
  description: string;
  category: string;
  status: string;
  priority: string;
  admin_response?: string;
  created_at: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
}

const formatDateTime = (dateString?: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

type SupportView = 'tickets' | 'chats' | 'aide';
const SUPPORT_VIEWS: SupportView[] = ['tickets', 'chats', 'aide'];

const ManagementSupport = () => {
  const { token } = useAuth();
  // Deux choses cohabitent ici : les tickets (formulaire, réponse par email)
  // et les discussions de l'app (répondues depuis Telegram, relues ici).
  // L'onglet se lit dans l'URL (`?vue=aide`) : l'Analytics de l'app y renvoie.
  const [viewParams, setViewParams] = useSearchParams();
  const view: SupportView = SUPPORT_VIEWS.includes(viewParams.get('vue') as SupportView) ? viewParams.get('vue') as SupportView : 'tickets';
  const setView = (next: SupportView) => {
    const updated = new URLSearchParams(viewParams);
    if (next === 'tickets') updated.delete('vue'); else updated.set('vue', next);
    setViewParams(updated, { replace: true });
  };
  const [openChats, setOpenChats] = useState(0);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chip, setChip] = useState<Chip>('todo');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selected, setSelected] = useState<Ticket | null>(null);
  // Ticket désigné par la cloche : on l'ouvre directement, sinon le clic
  // n'aurait fait que déposer l'utilisateur devant la liste complète.
  const [searchParams] = useSearchParams();
  const focusTicketId = searchParams.get('ticket');
  const [response, setResponse] = useState('');
  const [busy, setBusy] = useState(false);
  const [responseSent, setResponseSent] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [linkedRental, setLinkedRental] = useState<any | null>(null);
  const [loadingRental, setLoadingRental] = useState(false);

  // Création manuelle — pour un signalement reçu par téléphone/email plutôt
  // que soumis depuis l'app.
  const [users, setUsers] = useState<AppUser[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [newUser, setNewUser] = useState<AppUser | null>(null);
  const [newSubject, setNewSubject] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('other');
  const [newPriority, setNewPriority] = useState('normal');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const resetCreateForm = () => {
    setShowCreate(false);
    setUserQuery('');
    setNewUser(null);
    setNewSubject('');
    setNewDescription('');
    setNewCategory('other');
    setNewPriority('normal');
    setCreateError(null);
  };

  const filteredUsers = useMemo(() => {
    if (!userQuery.trim()) return users.slice(0, 8);
    const needle = userQuery.toLowerCase();
    return users.filter((u) =>
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(needle) || u.email?.toLowerCase().includes(needle)
    ).slice(0, 8);
  }, [users, userQuery]);

  const canCreate = !!newUser && newSubject.trim().length > 0 && newDescription.trim().length > 0 && !creating;

  const submitTicket = async () => {
    if (!token || !canCreate || !newUser) return;
    setCreating(true);
    setCreateError(null);
    try {
      await createTicket(
        { user_id: newUser.id_user, subject: newSubject.trim(), description: newDescription.trim(), category: newCategory, priority: newPriority },
        token
      );
      resetCreateForm();
      load();
    } catch (err: any) {
      setCreateError(err.message || 'Échec de la création du ticket.');
    } finally {
      setCreating(false);
    }
  };

  const load = () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    getAllTickets(token)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setTickets(list);
        if (focusTicketId) {
          const wanted = list.find((t: Ticket) => t.id_ticket === focusTicketId);
          if (wanted) setSelected(wanted);
        }
      })
      .catch((err: any) => setError(err.message || 'Erreur lors du chargement des tickets'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (token) {
      getAllUsersWithToken(token).then((data: any) => setUsers(Array.isArray(data) ? data : [])).catch(() => {});
      // Le compteur de l'onglet : une discussion en cours sans réponse doit
      // se voir depuis les tickets, pas seulement une fois l'onglet ouvert.
      getSupportConversations(token, 'open')
        .then((rows) => setOpenChats(Array.isArray(rows) ? rows.length : 0))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      const done = t.status === 'resolved' || t.status === 'closed';
      if (chip === 'todo' && done) return false;
      if (chip === 'urgent' && (done || t.priority !== 'urgent')) return false;
      if (chip === 'in_progress' && t.status !== 'in_progress') return false;
      if (chip === 'done' && !done) return false;
      if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
      return true;
    });
  }, [tickets, chip, categoryFilter]);

  const stats = useMemo(() => ({
    open: tickets.filter((t) => t.status === 'open').length,
    inProgress: tickets.filter((t) => t.status === 'in_progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved' || t.status === 'closed').length,
    urgent: tickets.filter((t) => t.priority === 'urgent' && t.status !== 'resolved' && t.status !== 'closed').length,
  }), [tickets]);

  const openTicket = (t: Ticket) => {
    setSelected(t);
    setResponse(t.admin_response || '');
    setResponseSent(false);
    setUpdateError(null);
    setLinkedRental(null);
    if (t.rental_id && token) {
      setLoadingRental(true);
      getRentalById(t.rental_id, token)
        .then(setLinkedRental)
        .catch(() => setLinkedRental(null))
        .finally(() => setLoadingRental(false));
    }
  };

  const applyUpdate = async (data: { status?: string; priority?: string; admin_response?: string }) => {
    if (!token || !selected) return;
    setBusy(true);
    setUpdateError(null);
    try {
      const updated = await updateTicket(selected.id_ticket, data, token);
      setTickets((prev) => prev.map((t) => (t.id_ticket === selected.id_ticket ? { ...t, ...updated } : t)));
      setSelected((prev) => (prev ? { ...prev, ...updated } : prev));
      if (data.admin_response) {
        setResponseSent(true);
        setTimeout(() => setResponseSent(false), 4000);
      }
    } catch (err: any) {
      setUpdateError(err.message || 'Échec de la mise à jour.');
    } finally {
      setBusy(false);
    }
  };

  const tabs = (
    <>
      <HeaderActions>
        <Segmented ariaLabel="Support" value={view} onChange={(id) => setView(id as SupportView)}
          options={[
            { id: 'chats', label: `Discussions${openChats ? ` · ${openChats}` : ''}` },
            { id: 'tickets', label: `Tickets${stats.open ? ` · ${stats.open}` : ''}` },
            { id: 'aide', label: 'Aide de l’app' }
          ]} />
      </HeaderActions>
      <div className="lg:hidden">
        <Segmented ariaLabel="Support" value={view} onChange={(id) => setView(id as SupportView)}
          options={[{ id: 'chats', label: 'Discussions' }, { id: 'tickets', label: 'Tickets' }, { id: 'aide', label: 'Aide' }]} />
      </div>
    </>
  );

  if (view === 'aide') {
    return (
      <main className="flex-1 space-y-5 overflow-y-auto bg-[#F7F7FB] px-4 pb-6 pt-1 lg:px-7">
        {tabs}
        <HelpFeedback />
      </main>
    );
  }

  if (view === 'chats') {
    return (
      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden bg-[#F7F7FB] px-4 pb-6 pt-1 lg:px-7">
        {tabs}
        <SupportConversations />
      </main>
    );
  }

  const selectedUser = selected ? users.find((u) => u.id_user === selected.user_id) : undefined;
  const todo = stats.open + stats.inProgress;

  return (
    <main className="flex min-h-0 flex-1 gap-5 overflow-hidden bg-[#F7F7FB] px-4 pb-6 pt-1 lg:px-7">
      {tabs}
      <HeaderActions>
        <button type="button" onClick={() => setShowCreate(true)}
          className="inline-flex h-10 items-center gap-1.5 rounded-[20px] bg-[#4F46E5] px-4 text-[13.5px] font-bold text-white hover:bg-[#4338CA]">
          <LineIcon d="M12 5v14M5 12h14" size={16} color="#fff" width={2.4} />
          Nouveau ticket
        </button>
      </HeaderActions>

      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto [&>*]:shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <FilterChip active={chip === 'todo'} count={todo || null} onClick={() => setChip('todo')}>À traiter</FilterChip>
          <FilterChip active={chip === 'urgent'} count={stats.urgent || null} countTone="red" onClick={() => setChip('urgent')}>Urgents</FilterChip>
          <FilterChip active={chip === 'in_progress'} onClick={() => setChip('in_progress')}>En cours</FilterChip>
          <FilterChip active={chip === 'done'} onClick={() => setChip('done')}>Résolus</FilterChip>
          <FilterChip active={chip === 'all'} onClick={() => setChip('all')}>Tous</FilterChip>
          <span className="flex-1" />
          <FilterChip active={filtersOpen} count={categoryFilter !== 'all' ? 1 : null} onClick={() => setFiltersOpen((v) => !v)}
            icon={<LineIcon d={ICONS.filter} size={14} color={filtersOpen ? '#fff' : '#4B5563'} />}>Filtrer</FilterChip>
        </div>
        {filtersOpen && (
          <Card className="mt-3 px-5 py-4">
            <SectionLabel>Catégorie</SectionLabel>
            <div className="mt-2 flex flex-wrap gap-2">
              <FilterChip active={categoryFilter === 'all'} onClick={() => setCategoryFilter('all')}>Toutes</FilterChip>
              {Object.entries(CATEGORY_LABELS).map(([k, l]) => <FilterChip key={k} active={categoryFilter === k} onClick={() => setCategoryFilter(k)}>{l}</FilterChip>)}
            </div>
          </Card>
        )}

        <Card className="mt-4 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              <div className={`grid ${COLS} gap-3 px-6 pb-2.5 pt-4`}>
                {['Ticket', 'Catégorie', 'Priorité', 'Statut', 'Reçu'].map((h) => <SectionLabel key={h}>{h}</SectionLabel>)}
                <span />
              </div>
              {error && <p className="border-t border-[#EEF0F4] px-6 py-4 text-[13px] text-[#B91C1C]">{error}</p>}
              {loading ? (
                <div className="space-y-2 border-t border-[#EEF0F4] p-4">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-[#EEF0F5]" />)}</div>
              ) : filtered.length === 0 ? (
                <p className="border-t border-[#EEF0F4] px-6 py-14 text-center text-[13px] text-[#6B7280]">{chip === 'todo' ? 'Aucun ticket à traiter.' : 'Aucun ticket ici.'}</p>
              ) : filtered.map((t) => {
                const active = selected?.id_ticket === t.id_ticket;
                const name = `${t.user_first_name || ''} ${t.user_last_name || ''}`.trim() || t.user_email;
                return (
                  <button key={t.id_ticket} type="button" onClick={() => (active ? setSelected(null) : openTicket(t))}
                    className={`grid w-full ${COLS} items-center gap-3 border-t border-[#EEF0F4] px-6 py-3 text-left transition-colors ${active ? 'bg-[#F5F4FE] shadow-[inset_4px_0_0_#4F46E5]' : 'hover:bg-[#FAFAFD]'}`}>
                    <span className="flex min-w-0 items-center gap-3">
                      <Avatar name={name} photo={photoUrl(users.find((u) => u.id_user === t.user_id)?.profile_photo)} size={34} tone={toneOf(t.user_id || t.id_ticket)} />
                      <span className="min-w-0">
                        <b className="block truncate text-[14px] font-bold text-[#1E293B]">{t.subject}</b>
                        <span className="block truncate text-[12.5px] text-[#6B7280]">{name}</span>
                      </span>
                    </span>
                    <span className="truncate text-[13px] text-[#1E293B]">{CATEGORY_LABELS[t.category] || t.category}</span>
                    <span><StatusPill tone={PRIORITY_TONE[t.priority] || 'grey'}>{PRIORITY_LABELS[t.priority] || t.priority}</StatusPill></span>
                    <span><StatusPill tone={STATUS_TONE[t.status] || 'grey'}>{STATUS_LABELS[t.status] || t.status}</StatusPill></span>
                    <span className="text-[13px] text-[#6B7280]">{ago(t.created_at)}</span>
                    <LineIcon d={ICONS.chevron} size={14} color="#9CA3AF" width={2.2} />
                  </button>
                );
              })}
            </div>
          </div>
          {!loading && filtered.length > 0 && (
            <div className="border-t border-[#EEF0F4] px-6 py-3.5 text-[13px] text-[#6B7280]">{filtered.length} {filtered.length > 1 ? 'tickets' : 'ticket'} sur {tickets.length}</div>
          )}
        </Card>
      </div>

      {/* Le ticket choisi, à droite */}
      {selected && (
        <aside className="hidden w-[380px] shrink-0 flex-col overflow-hidden rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] xl:flex">
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 pb-[18px] pt-[22px]">
              <div className="flex items-center gap-1.5">
                <StatusPill tone={PRIORITY_TONE[selected.priority] || 'grey'}>{PRIORITY_LABELS[selected.priority] || selected.priority}</StatusPill>
                <StatusPill tone={STATUS_TONE[selected.status] || 'grey'}>{STATUS_LABELS[selected.status] || selected.status}</StatusPill>
                <span className="ml-auto"><CloseButton onClick={() => setSelected(null)} /></span>
              </div>
              <h2 className="mt-3 text-[19px] font-extrabold leading-[1.3] text-[#1E293B]">{selected.subject}</h2>
              <div className="mt-3 flex items-center gap-2.5">
                <Avatar name={`${selected.user_first_name} ${selected.user_last_name}`} photo={photoUrl(selectedUser?.profile_photo)} size={32} tone={toneOf(selected.user_id || selected.id_ticket)} />
                <span className="min-w-0">
                  <b className="block truncate text-[14px] font-bold">{selected.user_first_name} {selected.user_last_name}</b>
                  <span className="block truncate text-[12.5px] text-[#6B7280]">{CATEGORY_LABELS[selected.category] || selected.category} · reçu {ago(selected.created_at)}</span>
                </span>
              </div>
            </div>
            <div className="h-2 bg-[#F1F2F8]" />
            <div className="px-6 py-[18px]">
              <SectionLabel>Description</SectionLabel>
              <p className="mt-2 whitespace-pre-wrap text-[13.5px] leading-[1.55] text-[#4B5563]">{selected.description}</p>
            </div>
            {selected.rental_id && (
              <>
                <div className="h-2 bg-[#F1F2F8]" />
                <div className="px-6 py-[18px]">
                  <SectionLabel>Location concernée</SectionLabel>
                  {loadingRental ? (
                    <div className="mt-2.5 h-14 animate-pulse rounded-xl bg-[#EEF0F5]" />
                  ) : !linkedRental ? (
                    <p className="mt-2 text-[13px] text-[#6B7280]">Location introuvable, peut-être supprimée.</p>
                  ) : (
                    <a href={`/mytrips?q=${encodeURIComponent(selected.rental_id.slice(0, 8))}`} className="mt-2.5 flex items-center gap-3 rounded-2xl transition-colors hover:bg-[#FAFAFD]">
                      <VehicleThumb vehicle={linkedRental.vehicle || linkedRental} type={linkedRental.vehicle_type} size={56} radius={14} />
                      <span className="min-w-0 flex-1">
                        <b className="block truncate text-[14px] font-bold">{[linkedRental.brand || linkedRental.vehicle?.brand, linkedRental.model || linkedRental.vehicle?.model].filter(Boolean).join(' ') || VEHICLE_TYPE_LABELS[linkedRental.vehicle_type] || 'Véhicule'}</b>
                        <span className="block truncate text-[12.5px] text-[#6B7280]">{formatDateTime(linkedRental.start_date)}{linkedRental.total_price ? ` · ${linkedRental.total_price} DT` : ''}</span>
                      </span>
                      <LineIcon d={ICONS.chevron} size={14} color="#9CA3AF" width={2.2} />
                    </a>
                  )}
                </div>
              </>
            )}
            <div className="h-2 bg-[#F1F2F8]" />
            <div className="px-6 py-[18px]">
              <SectionLabel>Réponse à l’utilisateur</SectionLabel>
              <textarea value={response} onChange={(e) => { setResponse(e.target.value); setResponseSent(false); }} rows={4}
                placeholder="Écrire la réponse envoyée par e-mail et dans l’app…" className={`${AREA} mt-2.5 text-[14px]`} />
              {responseSent && <p className="mt-2 text-[12.5px] font-semibold text-[#047857]">Réponse envoyée à {selected.user_first_name}.</p>}
              {updateError && <p className="mt-2 text-[12.5px] text-[#B91C1C]">{updateError}</p>}
              <div className="mt-3 [&>div]:flex [&>div]:w-full [&_button]:flex-1">
                <Segmented ariaLabel="Statut" value={selected.status === 'closed' ? 'resolved' : selected.status}
                  onChange={(id) => { if (!busy && id !== selected.status) applyUpdate({ status: id }); }}
                  options={[{ id: 'open', label: 'Ouvert' }, { id: 'in_progress', label: 'En cours' }, { id: 'resolved', label: 'Résolu' }]} />
              </div>
            </div>
          </div>
          <div className="flex gap-2.5 border-t border-[#EEF0F4] px-6 pb-5 pt-3.5">
            {selectedUser?.phone && (
              <a href={`tel:${selectedUser.phone}`} className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-3xl bg-[#F1F2F8] text-[15px] font-bold text-[#1E293B] hover:bg-[#E7E9F2]">
                <LineIcon d={ICONS.phone} size={16} color="#1E293B" />
                Appeler
              </a>
            )}
            <BigButton className="flex-[1.4] px-0" disabled={busy || !response.trim() || response.trim() === (selected.admin_response || '')}
              onClick={() => applyUpdate({ admin_response: response.trim() })}>
              {busy ? 'Envoi…' : 'Envoyer'}
            </BigButton>
          </div>
        </aside>
      )}

      {showCreate && (
        <Modal title="Nouveau ticket" subtitle="Pour un signalement reçu par téléphone ou par e-mail." onClose={resetCreateForm} width={600}>
          <FieldLabel className="mt-6">Personne</FieldLabel>
          {newUser ? (
            <div className={`${FIELD} flex items-center gap-3`}>
              <Avatar name={`${newUser.first_name} ${newUser.last_name}`} photo={photoUrl(newUser.profile_photo)} size={30} tone={toneOf(newUser.id_user)} />
              <b className="min-w-0 flex-1 truncate text-[14px]">{newUser.first_name} {newUser.last_name}</b>
              <LinkButton onClick={() => setNewUser(null)}>Changer</LinkButton>
            </div>
          ) : (
            <div className="relative">
              <input autoFocus value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder="Chercher par nom ou e-mail…" className={FIELD} />
              {userQuery.trim() && (
                <div className="absolute left-0 right-0 top-full z-10 mt-1.5 max-h-64 overflow-y-auto rounded-2xl border border-[#EEF0F4] bg-white py-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.15)]">
                  {filteredUsers.length === 0 ? <p className="px-4 py-2 text-[13px] text-[#6B7280]">Aucun compte trouvé.</p> : filteredUsers.map((u) => (
                    <button key={u.id_user} type="button" onClick={() => { setNewUser(u); setUserQuery(''); }} className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-[#F7F7FB]">
                      <Avatar name={`${u.first_name} ${u.last_name}`} photo={photoUrl(u.profile_photo)} size={30} tone={toneOf(u.id_user)} />
                      <span className="min-w-0"><b className="block truncate text-[13.5px]">{u.first_name} {u.last_name}</b><span className="block truncate text-[12px] text-[#6B7280]">{u.email}</span></span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <FieldLabel htmlFor="t-subject">Sujet</FieldLabel>
          <input id="t-subject" value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder="Ex. Véhicule non démarré au rendez-vous" className={FIELD} />
          <FieldLabel htmlFor="t-desc">Description</FieldLabel>
          <textarea id="t-desc" value={newDescription} onChange={(e) => setNewDescription(e.target.value)} rows={4} placeholder="Ce qui s’est passé, avec l’heure et le lieu…" className={AREA} />
          <FieldLabel>Catégorie</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {Object.entries(CATEGORY_LABELS).map(([k, l]) => <FilterChip key={k} active={newCategory === k} onClick={() => setNewCategory(k)}>{l}</FilterChip>)}
          </div>
          <FieldLabel>Priorité</FieldLabel>
          <div className="[&>div]:flex [&>div]:w-full [&_button]:flex-1">
            <Segmented ariaLabel="Priorité" value={newPriority} onChange={setNewPriority} options={Object.entries(PRIORITY_LABELS).map(([id, label]) => ({ id, label }))} />
          </div>
          {createError && <p className="mt-4 rounded-xl bg-[#FDECEF] px-3 py-2 text-[13px] text-[#B91C1C]">{createError}</p>}
          <div className="mt-6 flex gap-3">
            <BigButton kind="secondary" onClick={resetCreateForm} className="flex-1">Annuler</BigButton>
            <BigButton onClick={submitTicket} disabled={!canCreate} className="flex-1">{creating ? 'Création…' : 'Créer le ticket'}</BigButton>
          </div>
        </Modal>
      )}
    </main>
  );
};

export default ManagementSupport;
